import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider, Typography } from "@mui/material";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { gateway } from "../../gateway";
import { getEnvVar } from "../../utils/get-env";
import { colors } from "../ui/colors";

type CardStatus = {
  hasPaymentMethod: boolean;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  consentAt?: string | null;
};

type SetupResponse = {
  clientSecret: string;
  setupIntentId: string;
  connectedAccountId: string;
};

const publishableKey = getEnvVar("REACT_APP_STRIPE_PUBLISHABLE_KEY") || "";
const consentText = getEnvVar("REACT_APP_CLIENT_CARD_CONSENT_TEXT") || "";
const consentVersion = getEnvVar("REACT_APP_CLIENT_CARD_CONSENT_VERSION") || "";

const primaryButtonSx = {
  alignSelf: "center",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8125rem",
  borderRadius: "10px",
  px: 3,
  minHeight: 36,
  bgcolor: colors.orange[500],
  "&:hover": { bgcolor: colors.orange[600] },
};

function formatConsentDate(isoString?: string | null) {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return new Date(isoString).toLocaleDateString();
  }
}

function SavedCardView({ status, onReplace }: { status: CardStatus; onReplace: () => void }) {
  const brandLabel = (status.brand || "Card").toUpperCase();
  const expiry = status.expMonth && status.expYear ? `${String(status.expMonth).padStart(2, "0")}/${status.expYear}` : null;

  return (
    <Box
      sx={{
        borderRadius: "12px",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        p: { xs: 2, sm: 2.25 },
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
      }}
    >
      {/* Top Header: Method Pill + Status Badge */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
        {/* Interactive Pill matching Flow Mobile Subscriptions */}
        <Box
          onClick={onReplace}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onReplace();
            }
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              borderColor: colors.orange[500],
              boxShadow: "0 4px 12px rgba(251, 142, 0, 0.15)",
              transform: "translateY(-1px)",
              "& .update-text": {
                color: colors.orange[500],
              },
            },
          }}
        >
          <CreditCardOutlinedIcon sx={{ fontSize: 18, color: "#475569" }} />
          <Typography sx={{ fontSize: "0.8125rem", color: "#1E293B", fontWeight: 700, letterSpacing: "0.01em" }}>
            {brandLabel} •••• {status.last4 || "••••"}
          </Typography>

          <Divider orientation="vertical" flexItem sx={{ my: 0.25, borderColor: "divider" }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              className="update-text"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                color: colors.orange[500],
                transition: "color 0.2s ease",
              }}
            >
              Update
            </Typography>
            <EditOutlinedIcon
              className="update-text"
              sx={{
                fontSize: 14,
                color: colors.orange[500],
                transition: "color 0.2s ease",
              }}
            />
          </Box>
        </Box>

        {/* Status Badge */}
        <Chip
          label="ACTIVE"
          size="small"
          sx={{
            bgcolor: "#DCFCE7",
            color: "#15803D",
            fontWeight: 800,
            fontSize: "0.68rem",
            borderRadius: "8px",
            height: 24,
            px: 0.5,
          }}
        />
      </Box>

      <Divider sx={{ borderColor: "divider" }} />

      {/* Structured Details: Card Brand, Expiration, Status */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" }, gap: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
            Card Brand
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A", textTransform: "capitalize" }}>
            {status.brand || "Credit Card"}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
            Expires
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>
            {expiry || "—"}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
            Status
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#15803D" }}>
            Ready for charges
          </Typography>
        </Box>
      </Box>

      {status.consentAt && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pt: 0.25 }}>
          <LockOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
            Authorized on {formatConsentDate(status.consentAt)} · Stored securely in connected Stripe account
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function CardForm({ clientUid, setupIntentId, onSaved }: { clientUid: string; setupIntentId: string; onSaved: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!stripe || !elements || saving) return;
    setSaving(true);
    setError("");
    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (result.error) throw new Error(result.error.message || "Stripe could not save this card.");
      if (result.setupIntent?.status !== "succeeded" || result.setupIntent.id !== setupIntentId) {
        throw new Error("Stripe has not completed card authorization. Please try again.");
      }
      await gateway.post(`/client-payment-methods/${clientUid}/payment-method/confirm`, { setupIntentId });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "The card could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PaymentElement />
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%", mt: 1 }}>
        <Button variant="contained" disableElevation onClick={save} disabled={!stripe || !elements || saving} sx={primaryButtonSx}>
          {saving ? <CircularProgress size={18} color="inherit" /> : "Save card"}
        </Button>
      </Box>
    </Box>
  );
}

export function ClientPaymentMethodTab({ clientUid }: { clientUid: string }) {
  const [status, setStatus] = useState<CardStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [setup, setSetup] = useState<SetupResponse | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [isReplacing, setIsReplacing] = useState(false);
  const termsReady = Boolean(consentText.trim() && /^[a-zA-Z0-9._-]{1,64}$/.test(consentVersion));

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await gateway.get<CardStatus>(`/client-payment-methods/${clientUid}/payment-method`);
      setStatus(response.data);
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.message || "Could not load the saved card status. Connect the company's Stripe account first.");
    } finally {
      setLoading(false);
    }
  }, [clientUid]);

  useEffect(() => {
    setSetup(null);
    setAccepted(false);
    setIsReplacing(false);
    loadStatus();
  }, [loadStatus]);

  const stripePromise = useMemo(
    () => (publishableKey && setup?.connectedAccountId ? loadStripe(publishableKey, { stripeAccount: setup.connectedAccountId }) : null),
    [setup?.connectedAccountId],
  );

  const startSetup = async () => {
    if (!accepted || !termsReady || starting || !publishableKey) return;
    setStarting(true);
    setError("");
    try {
      const response = await gateway.post<SetupResponse>(`/client-payment-methods/${clientUid}/payment-method/setup-intent`, {
        consentAccepted: true,
        consentVersion,
      });
      if (!response.data?.clientSecret || !response.data?.setupIntentId || !response.data?.connectedAccountId) {
        throw new Error("Stripe setup information is incomplete.");
      }
      setSetup(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Could not start card setup.");
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
        <CircularProgress size={24} sx={{ color: colors.orange[500] }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560, width: "100%", mx: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.3 }}>Client payment method</Typography>
        <Typography sx={{ mt: 0.75, fontSize: "0.8125rem", color: "text.secondary", lineHeight: 1.5 }}>
          The card is stored by the company's connected account. FlowNect never receives the card number or security code.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {status?.hasPaymentMethod && !isReplacing ? (
        <SavedCardView status={status} onReplace={() => setIsReplacing(true)} />
      ) : (
        <>
          {status?.hasPaymentMethod && isReplacing && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>Enter new card details</Typography>
              <Button
                size="small"
                onClick={() => {
                  setIsReplacing(false);
                  setSetup(null);
                }}
                sx={{ textTransform: "none", color: "text.secondary", fontWeight: 600 }}
              >
                Cancel
              </Button>
            </Box>
          )}

          {!termsReady && (
            <Alert severity="warning">Card collection is unavailable until approved authorization terms and their version are configured.</Alert>
          )}

          {termsReady && !setup && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  border: "1px solid",
                  borderColor: accepted ? colors.orange[500] : "divider",
                  borderRadius: "12px",
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Checkbox
                  checked={accepted}
                  onChange={(event) => setAccepted(event.target.checked)}
                  sx={{ p: 0.5, color: colors.orange[500], "&.Mui-checked": { color: colors.orange[500] } }}
                />
                <Box sx={{ pt: 0.5 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.4 }}>
                    I confirm the client authorized this card to be saved for future payments.
                  </Typography>
                  <Typography sx={{ mt: 0.75, fontSize: "0.8125rem", color: "text.secondary", lineHeight: 1.5 }}>{consentText}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", width: "100%", mt: 1 }}>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={startSetup}
                  disabled={!accepted || starting || !publishableKey}
                  sx={primaryButtonSx}
                >
                  {starting ? <CircularProgress size={18} color="inherit" /> : "Enter card securely"}
                </Button>
              </Box>
            </>
          )}

          {setup && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret: setup.clientSecret }}>
              <CardForm
                clientUid={clientUid}
                setupIntentId={setup.setupIntentId}
                onSaved={() => {
                  setSetup(null);
                  setIsReplacing(false);
                  loadStatus();
                }}
              />
            </Elements>
          )}
        </>
      )}
    </Box>
  );
}
